import React, { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { useParams, useLocation } from "react-router-dom";

import useGetTeamMemberInvitation from "hooks/api/invitation/useGetTeamMemberInvitation";
import useSaveAcceptInvitation from "hooks/api/invitation/useSaveAcceptInvitation";
import useSession from "hooks/api/auth/useSession";
import { redirectToAuthPortal } from "utils/authPortal";
import actions from "actions";
import {ANLoadingScreen } from "@answersnow/pkg-theme";
import UpdateMemberInfo from "./UpdateMemberInfo";
import ResetPassword from "../ResetPassword/ResetPassword";
import SplashScreen from "./SplashScreen";
import { ClientCookie } from "./utils";

const currentDate = new Date();
const cookieClientId = new ClientCookie();

export default function AcceptInvitation() {
  const [step, setStep] = useState("setPassword");
  const [userInfo, setUserInfo] = useState({});

  const dispatch = useDispatch();
  const { guid } = useParams();
  // Get the invitation id from the URL
  const searchParams = new URLSearchParams(useLocation().search);
  const invitationId = searchParams.get("invitationid") || null;

  // Dispatch actions
  const setPageDetails = (payload) => dispatch(actions.setPageDetails(payload));

  useEffect(() => {
    setPageDetails({
      pageName: "Accept Invitation",
    });

    if (!invitationId || !guid) {
      location.href = "/";
    }
  }, []);

  /* ----- Mutations ----- */
  let { acceptInvitationMutation, isMutationInvitationIdle, isMutationInvitationLoading } =
    useSaveAcceptInvitation({
      onSuccess: (userData) => {
        // Existing users with a Cognito account go home; all others set up their profile
        const isMemberExists = invitation?.existingUserId && invitation?.hasCognitoAccount;
        if (isMemberExists) {
          location.href = "/";
          return;
        }

        // New users: the backend set their password AND established a session
        // cookie on accept (no client-side Cognito sign-in needed), so we can
        // go straight to profile completion.
        setUserInfo({ username: userData.data.email, userId: userData.data.userId });
        setStep("updateInfo");
      },
    });

  /* 1. ---- Fetch Invitation details ---- */
  let {
    invitation,
    invitationLoading,
    error: invitationErrorData,
    isSuccess: invitationSuccess,
  } = useGetTeamMemberInvitation({ guid, invitationId });
  let { session, sessionLoading, sessionSuccess } = useSession(invitationSuccess);

  /* 2. ---- Display the splashScreen if the invitation has expired or already accepted ---- */
  const expirationDate = invitation?.userInvites?.expirationDate;
  const invitationHasExpired = expirationDate && currentDate > new Date(expirationDate);
  const invitationWasAccepted = invitation?.userInvites?.acceptedAt;

  if (invitationErrorData) {
    console.error(invitationErrorData);
    location.href = "/";
  }

  const showSplashScreen =
    (invitationHasExpired || invitationWasAccepted) && step === "setPassword";

  if (showSplashScreen) {
    return (
      <SplashScreen
        title="This link has expired"
        description={
          <>
            Questions or Issues? Contact us at <br />
            (800) 486-9060.
          </>
        }
        alertSeverity="error"
      />
    );
  }

  /* 3. ---- If the invited Member has a Cognito account, redirect to login: ---- */
  const userExists = Boolean(invitation?.existingUserId && invitation?.hasCognitoAccount);

  if (invitationSuccess && sessionSuccess && step === "setPassword") {
    if (!cookieClientId.get() && !session && userExists) {
      cookieClientId.set({
        clientId: invitation?.clientId,
        userId: invitation?.existingUserId,
      });
      cookieClientId.save();
    }

    if (!session && userExists) {
      // Existing user isn't logged in — send them to the auth portal, returning
      // to this invitation page afterward so acceptance can complete.
      // /accept-invitation is itself an in-app public route (so
      // redirectToAuthPortal no-ops there by default), but an existing user
      // who isn't logged in must still be bounced to the portal to
      // authenticate before we can complete the invitation acceptance.
      redirectToAuthPortal({ force: true });
    }

    // Fix avoid creating a new user if there is an logged in user
    if (session && !userExists) {
      location.href = "/";
    }

    /* 4. ---- If the existing Member is logged in, check credentials and accept invitation ---- */
    const isCredentialsMatch = session?.email === invitation?.userInvites?.email;

    if (userExists && session && isCredentialsMatch && isMutationInvitationIdle) {
      acceptInvitationMutation({
        id: invitationId,
        invitationGuid: guid,
      });
    }

    // Redirect to the home page if the credentials do not match
    if (userExists && session && !isCredentialsMatch) {
      console.error("The credentials do not match");
      location.href = "/";
    }
  }

  /* 5. New invited member sets a password — the backend creates the account,
        sets the password, and establishes a session, then we collect profile info. */
  const handleCreatePassword = (values) => {
    acceptInvitationMutation({
      id: invitationId,
      invitationGuid: guid,
      password: values.password,
    });
  };

  // Display the loading screen while fetching or processing the data
  const loading =
    invitationLoading || sessionLoading || (isMutationInvitationLoading && userExists);

  if (loading) return <ANLoadingScreen />;

  return (
    <div>
      {step === "setPassword" ? (
        <ResetPassword
          change={true}
          invitationSignin={true}
          loginInvitation={{
            email: invitation?.userInvites?.email,
          }}
          onChange={() => {}}
          onSubmitInvitationLogin={handleCreatePassword}
          showVersion={false}
          username={invitation?.userInvites?.email}
          verificationCode="ignoreme"
        />
      ) : (
        <UpdateMemberInfo
          email={userInfo.username}
          userId={userInfo.userId}
          clientId={invitation?.clientId}
        />
      )}
    </div>
  );
}
